import type { Session } from "@supabase/supabase-js";

type AuthResult = {
  data: { session: Session | null };
  error: { message: string } | null;
};

type PasswordUpdateAuth = {
  getSession: () => Promise<AuthResult>;
  exchangeCodeForSession: (code: string) => Promise<AuthResult>;
  setSession: (tokens: {
    access_token: string;
    refresh_token: string;
  }) => Promise<AuthResult>;
  verifyOtp: (credentials: {
    token_hash: string;
    type: "invite" | "recovery";
  }) => Promise<AuthResult>;
};

export type PasswordUpdateSessionResult = {
  session: Session | null;
  error: string;
  source: "code" | "tokens" | "token_hash" | "stored" | "missing";
};

const decodedUrlError = (url: URL, hash: URLSearchParams) => {
  const description =
    hash.get("error_description") ||
    url.searchParams.get("error_description") ||
    hash.get("error") ||
    url.searchParams.get("error");
  return description ? description.replace(/\+/g, " ") : "";
};

export async function establishPasswordUpdateSession(
  auth: PasswordUpdateAuth,
  href: string,
): Promise<PasswordUpdateSessionResult> {
  const url = new URL(href),
    hash = new URLSearchParams(url.hash.replace(/^#/, "")),
    urlError = decodedUrlError(url, hash),
    code = url.searchParams.get("code"),
    accessToken = hash.get("access_token"),
    refreshToken = hash.get("refresh_token"),
    tokenHash = url.searchParams.get("token_hash") || hash.get("token_hash"),
    type = url.searchParams.get("type") || hash.get("type");

  if (urlError) {
    return { session: null, error: urlError, source: "missing" };
  }

  if (code) {
    const result = await auth.exchangeCodeForSession(code);
    return {
      session: result.data.session,
      error:
        result.error?.message ||
        (result.data.session ? "" : "Le lien n’a pas créé de session sécurisée."),
      source: "code",
    };
  }

  if (accessToken && refreshToken) {
    const result = await auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    return {
      session: result.data.session,
      error:
        result.error?.message ||
        (result.data.session ? "" : "Le lien n’a pas créé de session sécurisée."),
      source: "tokens",
    };
  }

  if (tokenHash && (type === "invite" || type === "recovery")) {
    const result = await auth.verifyOtp({ token_hash: tokenHash, type });
    return {
      session: result.data.session,
      error:
        result.error?.message ||
        (result.data.session ? "" : "Le lien n’a pas créé de session sécurisée."),
      source: "token_hash",
    };
  }

  const stored = await auth.getSession();
  return {
    session: stored.data.session,
    error:
      stored.error?.message ||
      (stored.data.session
        ? ""
        : "Ce lien n’est plus rattaché à une session. Demandez un nouveau lien d’activation."),
    source: stored.data.session ? "stored" : "missing",
  };
}
